import { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react'
import './styles.css'

const AuthorizationTypeSettings = forwardRef(({ authorizationTypes, onAuthorizationTypesChange, chartType, onChartTypeChange, modalidades = [] }, ref) => {
  const MAX_MODALIDADES = 10

  // Generar tipos dinámicamente a partir de las modalidades recibidas
  const allTypes = modalidades.length > 0 ? modalidades.map(modalidad => ({
    name: modalidad.nombre,
    key: modalidad.key || `modalidad_${modalidad.id}`,
    displayName: modalidad.nombre,
    id: modalidad.id
  })) : []

  // Inicializar con todos los tipos como true dinámicamente
  const getDefaultTypes = () => {
    const defaultTypes = {}
    allTypes.forEach(type => {
      defaultTypes[type.key] = true
    })
    return defaultTypes
  }

  const [tempTypes, setTempTypes] = useState(() => {
    if (authorizationTypes && Object.keys(authorizationTypes).length > 0) {
      return authorizationTypes
    }
    return getDefaultTypes()
  })
  const [tempChartType, setTempChartType] = useState(chartType || 'lineal')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isChipsExpanded, setIsChipsExpanded] = useState(false)
  const [hasMoreThanTwoLines, setHasMoreThanTwoLines] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState('bottom')
  const dropdownRef = useRef(null)
  const dropdownTriggerRef = useRef(null)
  const chipsContainerRef = useRef(null)

  // Actualizar tempTypes cuando cambien las modalidades
  useEffect(() => {
    if (modalidades.length > 0) {
      // Si hay modalidades nuevas, reinicializar con todas seleccionadas
      setTempTypes(getDefaultTypes())
    } else if (authorizationTypes && Object.keys(authorizationTypes).length > 0) {
      setTempTypes(authorizationTypes)
    } else {
      setTempTypes(getDefaultTypes())
    }
  }, [modalidades, authorizationTypes])

  useEffect(() => {
    if (chartType) {
      setTempChartType(chartType)
    }
  }, [chartType])

  // Calcular si hay más de 2 filas de chips
  useEffect(() => {
    const checkLines = () => {
      if (!chipsContainerRef.current) return

      const container = chipsContainerRef.current
      const chips = container.querySelectorAll('.chip')

      if (chips.length === 0) {
        setHasMoreThanTwoLines(false)
        return
      }

      // Primero, expandir temporalmente para medir la altura real
      const wasCollapsed = container.classList.contains('authorization-tags-container--collapsed')
      container.classList.remove('authorization-tags-container--collapsed')

      // Esperar un frame para que el navegador calcule el layout
      requestAnimationFrame(() => {
        const containerHeight = container.offsetHeight

        // Calcular la altura de 2 filas
        if (chips.length > 0) {
          const firstChip = chips[0]
          const chipHeight = firstChip.offsetHeight
          const gap = 8

          // Altura de 2 filas: 2 chips + 1 gap entre filas
          const twoLinesHeight = (chipHeight * 2) + gap

          // Si la altura real es mayor que 2 filas, mostrar el botón
          setHasMoreThanTwoLines(containerHeight > twoLinesHeight + 1) // +1 para tolerancia
        }

        // Restaurar el estado colapsado si estaba colapsado
        if (wasCollapsed && !isChipsExpanded) {
          container.classList.add('authorization-tags-container--collapsed')
        }
      })
    }

    // Verificar después de que el componente se renderice
    const timeoutId = setTimeout(checkLines, 100)

    // También verificar cuando cambie el tamaño de la ventana
    window.addEventListener('resize', checkLines)

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('resize', checkLines)
    }
  }, [tempTypes, isChipsExpanded])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          dropdownTriggerRef.current && !dropdownTriggerRef.current.contains(event.target)) {
        setIsDropdownOpen(false)
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen])

  useImperativeHandle(ref, () => ({
    apply: () => {
      onAuthorizationTypesChange(tempTypes)
      if (onChartTypeChange) {
        onChartTypeChange(tempChartType)
      }
    }
  }))

  const handleRemoveType = (typeKey) => {
    setTempTypes(prev => ({
      ...prev,
      [typeKey]: false
    }))
  }

  const handleToggleType = (typeKey, isVisible) => {
    if (isVisible && selectedTypes.length >= MAX_MODALIDADES) {
      alert(`Solo puedes seleccionar un máximo de ${MAX_MODALIDADES} modalidades`)
      return
    }

    setTempTypes(prev => ({
      ...prev,
      [typeKey]: isVisible
    }))
  }

  const handleDropdownToggle = (e) => {
    if (!isDropdownOpen) {
      // Calcular si hay espacio suficiente abajo
      const rect = e.currentTarget.getBoundingClientRect()
      const dropdownHeight = 200 // altura máxima del dropdown
      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top

      // Si no hay suficiente espacio abajo pero sí arriba, posicionar arriba
      setDropdownPosition(spaceBelow < dropdownHeight && spaceAbove > dropdownHeight ? 'top' : 'bottom')
    }
    setIsDropdownOpen(!isDropdownOpen)
  }

  const removeModalidad = (typeKey) => {
    setTempTypes(prev => ({
      ...prev,
      [typeKey]: false
    }))
  }

  const selectedTypes = allTypes.filter(t => tempTypes[t.key] !== false)
  const availableTypes = allTypes.filter(t => tempTypes[t.key] === false)
  const currentlySelectedCount = selectedTypes.length

  return (
    <div className="authorization-settings">
      <div className="authorization-chart-section">
        <label className="authorization-chart-label">Tipo de Gráfico</label>
        <div className="authorization-chart-options">
          <label className="radio-option">
            <input
              type="radio"
              name="chartType"
              value="barras"
              checked={tempChartType === 'barras'}
              onChange={(e) => setTempChartType(e.target.value)}
            />
            <span className="radio-label">Barras</span>
          </label>
          <label className="radio-option">
            <input
              type="radio"
              name="chartType"
              value="lineal"
              checked={tempChartType === 'lineal'}
              onChange={(e) => setTempChartType(e.target.value)}
            />
            <span className="radio-label">Lineal</span>
          </label>
        </div>
      </div>

      {/* <div className="authorization-section">
        <h3 className="authorization-section-title">Tipos de modalidad</h3>
        <p className="authorization-selected-label">
          Tipos de modalidad seleccionados ({selectedTypes.length} de {MAX_MODALIDADES})
        </p>

        Modalidades seleccionadas como tags
        {selectedTypes.length > 0 && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            marginBottom: '24px'
          }}>
            {selectedTypes.map(type => (
              <div
                key={type.key}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: '#f0f4f8',
                  border: '1px solid #d1d9e0',
                  borderRadius: '16px',
                  padding: '6px 12px',
                  fontSize: '13px',
                  color: '#4a5568',
                  gap: '6px'
                }}
              >
                <span>{type.displayName}</span>
                <button
                  onClick={() => removeModalidad(type.key)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#718096',
                    fontSize: '16px',
                    padding: '0',
                    lineHeight: '1',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        <h4 style={{
          fontSize: '14px',
          fontWeight: '500',
          margin: '0 0 8px 0',
          color: '#333'
        }}>
          Selecciona tipos
        </h4>

        Selector múltiple
        <div style={{ position: 'relative' }} ref={dropdownRef}>
          <div
            ref={dropdownTriggerRef}
            onClick={handleDropdownToggle}
            style={{
              border: '1px solid #d1d9e0',
              borderRadius: '6px',
              padding: '10px 12px',
              cursor: 'pointer',
              backgroundColor: '#fff',
              minHeight: '20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '14px'
            }}
          >
            <span style={{ color: '#718096' }}>
              Selecciona opciones
            </span>
            <span style={{
              fontSize: '12px',
              color: '#718096',
              transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s'
            }}>
              ▼
            </span>
          </div>

          {isDropdownOpen && (
            <div
              style={{
                position: 'relative',
                ...(dropdownPosition === 'top'
                  ? { bottom: '100%', marginBottom: '2px', borderRadius: '6px' }
                  : { top: '100%', marginTop: '2px', borderRadius: '6px' }
                ),
                left: 0,
                right: 0,
                backgroundColor: '#fff',
                border: '1px solid #d1d9e0',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                maxHeight: '200px',
                overflowY: 'auto',
                zIndex: 99999
              }}
            >
              {allTypes.map(type => {
                const isSelected = tempTypes[type.key] === true
                const canSelect = !isSelected && selectedTypes.length < MAX_MODALIDADES

                return (
                  <label
                    key={type.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px 12px',
                      cursor: isSelected || canSelect ? 'pointer' : 'not-allowed',
                      backgroundColor: '#fff',
                      opacity: isSelected || canSelect ? 1 : 0.6,
                      borderBottom: '1px solid #f1f3f5',
                      fontSize: '14px',
                      color: '#4a5568'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected || false}
                      onChange={() => {
                        if (isSelected || canSelect) {
                          handleToggleType(type.key, !isSelected)
                        }
                      }}
                      disabled={!isSelected && !canSelect}
                      style={{
                        marginRight: '8px',
                        accentColor: '#3182ce'
                      }}
                    />
                    <span style={{ flex: 1 }}>{type.displayName}</span>
                    {!isSelected && !canSelect && (
                      <span style={{
                        fontSize: '12px',
                        color: '#a0aec0',
                        fontStyle: 'italic'
                      }}>
                        (Máximo {MAX_MODALIDADES})
                      </span>
                    )}
                  </label>
                )
              })}
            </div>
          )}
        </div>
      </div> */}
    </div>
  )
})

AuthorizationTypeSettings.displayName = 'AuthorizationTypeSettings'

export default AuthorizationTypeSettings
